import type { NormalizedRegionGeometry } from "./types";

export interface SupportPanelBounds {
  top: number;
  right: number;
  bottom: number;
  left: number;
  viewportHeight: number;
}

interface ReaderPageOcclusionInput {
  pageLeft: number;
  pageTop: number;
  pageWidth: number;
  pageHeight: number;
  region: NormalizedRegionGeometry;
  panel: SupportPanelBounds;
  clearance?: number;
}

const finite = (value: number) => Number.isFinite(value);

export function readerPageOcclusionShift({
  pageLeft,
  pageTop,
  pageWidth,
  pageHeight,
  region,
  panel,
  clearance = 12
}: ReaderPageOcclusionInput): number {
  if (![pageLeft, pageTop, pageWidth, pageHeight, region.x, region.y, region.width, region.height, panel.top, panel.right, panel.bottom, panel.left, panel.viewportHeight, clearance].every(finite)) return 0;
  if (pageWidth <= 0 || pageHeight <= 0 || region.width <= 0 || region.height <= 0 || clearance < 0) return 0;

  // The landscape Details card is a side surface. Only the bottom sheet shunts
  // the page; its width spans the reading surface and its top is the occlusion line.
  if (panel.bottom < panel.viewportHeight - 1) return 0;

  const pageBottom = pageTop + pageHeight;
  const normalizedRegionLeft = Math.max(0, Math.min(1, region.x));
  const normalizedRegionRight = Math.max(normalizedRegionLeft, Math.min(1, region.x + region.width));
  const normalizedRegionTop = Math.max(0, Math.min(1, region.y));
  const normalizedRegionBottom = Math.max(normalizedRegionTop, Math.min(1, region.y + region.height));
  const regionLeft = pageLeft + normalizedRegionLeft * pageWidth;
  const regionRight = pageLeft + normalizedRegionRight * pageWidth;
  const regionTop = pageTop + normalizedRegionTop * pageHeight;
  const regionBottom = pageTop + normalizedRegionBottom * pageHeight;

  if (regionRight <= panel.left || regionLeft >= panel.right) return 0;
  if (regionBottom <= panel.top || regionTop >= panel.bottom) return 0;

  const shiftNeededToClear = regionBottom - panel.top + clearance;
  const shiftThatFlushesPageBottom = Math.max(0, pageBottom - panel.top);
  return Math.max(0, Math.min(shiftNeededToClear, shiftThatFlushesPageBottom));
}
